import { createApi } from '@reduxjs/toolkit/query/react';

import { createApiBaseQuery, Methods } from '@app/api/shared';

import { RecommendChatEndpoints as Endpoints } from './types';

import type {
  AcknowledgeRecommendChatMessagesRequest,
  GetRecommendChatBindingConflictsRequest,
  GetRecommendChatSettingsRequest,
  RecommendAcknowledgeMessagesResult,
  RecommendBindingConflict,
  RecommendChatSettings,
  RecommendResolveBindingResult,
  ResolveRecommendChatBindingRequest,
  SaveRecommendChatSettingsRequest,
} from './types';

const RECOMMEND_REQUEST_TIMEOUT_MS = 30_000;

export const recommendChatApi = createApi({
  reducerPath: 'recommendChatApi',
  baseQuery: createApiBaseQuery('/RecommendChat'),
  tagTypes: ['RecommendSettings', 'RecommendBindingConflicts'],
  endpoints: ({ query, mutation }) => ({
    [Endpoints.GetSettings]: query<
      RecommendChatSettings,
      GetRecommendChatSettingsRequest
    >({
      query: (params) => ({
        url: 'Settings',
        method: Methods.GET,
        params,
        timeout: RECOMMEND_REQUEST_TIMEOUT_MS,
      }),
      providesTags: (_result, _error, { integrationId }) => [
        { type: 'RecommendSettings', id: integrationId },
      ],
    }),
    [Endpoints.SaveSettings]: mutation<
      RecommendChatSettings,
      SaveRecommendChatSettingsRequest
    >({
      query: ({ data, ...params }) => ({
        url: 'Settings',
        method: Methods.PUT,
        params,
        data,
        timeout: RECOMMEND_REQUEST_TIMEOUT_MS,
      }),
      invalidatesTags: (result) =>
        result ? [{ type: 'RecommendSettings', id: result.integrationId }] : [],
    }),
    [Endpoints.AcknowledgeMessages]: mutation<
      RecommendAcknowledgeMessagesResult,
      AcknowledgeRecommendChatMessagesRequest
    >({
      query: ({ data, ...params }) => ({
        url: 'AcknowledgeMessages',
        method: Methods.POST,
        params,
        data,
        timeout: RECOMMEND_REQUEST_TIMEOUT_MS,
      }),
    }),
    [Endpoints.GetBindingConflicts]: query<
      RecommendBindingConflict[],
      GetRecommendChatBindingConflictsRequest
    >({
      query: (params) => ({
        url: 'BindingConflicts',
        method: Methods.GET,
        params,
        timeout: RECOMMEND_REQUEST_TIMEOUT_MS,
      }),
      providesTags: ['RecommendBindingConflicts'],
    }),
    [Endpoints.ResolveBinding]: mutation<
      RecommendResolveBindingResult,
      ResolveRecommendChatBindingRequest
    >({
      query: ({ data, ...params }) => ({
        url: 'ResolveBinding',
        method: Methods.POST,
        params,
        data,
        timeout: RECOMMEND_REQUEST_TIMEOUT_MS,
      }),
      invalidatesTags: (result) =>
        result ? ['RecommendBindingConflicts'] : [],
    }),
  }),
});

export const {
  useGetRecommendChatSettingsQuery,
  useSaveRecommendChatSettingsMutation,
  useAcknowledgeRecommendChatMessagesMutation,
  useGetRecommendChatBindingConflictsQuery,
  useResolveRecommendChatBindingMutation,
} = recommendChatApi;
